import React, { useState, useEffect, useCallback } from "react";
import PageHeader from "../components/common/PageHeader";
import useStore from "../store/useStore";
import api from "../services/api";
import { resolveAssetUrl } from "../utils/assetUrl";

const formatStoredDate = (value) => {
  const match = String(value || "").match(/^(\d{4})-(\d{2})-(\d{2})/);
  if (!match) return "";
  return new Date(Number(match[1]), Number(match[2]) - 1, Number(match[3])).toLocaleDateString("en-GB");
};

const contactText = (value) => {
  const text = String(value || "").trim();
  if (!text || text.toUpperCase() === "N/A") return "";
  return text;
};

const Invoice = () => {
  const { toast } = useStore();

  const [tenants, setTenants] = useState([]);
  const [loading, setLoading] = useState(false);
  const [showInvoice, setShowInvoice] = useState(false);

  const [form, setForm] = useState({
    tenantId: "",
    rentalId: "",
    tenantName: "",
    assetName: "",
    location: "",
    unitNumber: "",
    billingPeriod: "",
    rentalCost: "",
    pendingPayment: "",
    dueDate: "",
    note: ""
  });

  const [invoiceData, setInvoiceData] = useState(null);

  const fetchTenants = useCallback(async () => {
    try {
      const res = await api.get('/tenants');
      setTenants(res.data.data || []);
    } catch (error) {
      console.error('Error fetching tenants:', error);
      toast('Failed to load tenants', 'error');
    }
  }, [toast]);

  useEffect(() => {
    fetchTenants();
  }, [fetchTenants]);

  const loadInvoice = async (tenantId, rentalId) => {
    if (!tenantId) {
      resetForm();
      return;
    }

    try {
      setLoading(true);

      const res = await api.get(`/invoices/tenant/${tenantId}`, {
        params: rentalId ? { rentalId } : {}
      });
      const data = res.data.data;
      const pending = Number(data.payment?.pendingPayment);
      const rentalCost = Number(data.payment?.totalDue);

      setForm({
        tenantId: String(tenantId),
        rentalId: data.rental?.id ? String(data.rental.id) : "",
        tenantName: data.tenant?.name || "",
        assetName: data.rental?.assetName || "",
        location: data.rental?.location || "",
        unitNumber: data.rental?.unitNumber || "",
        billingPeriod: data.rental?.billingPeriod || "",
        rentalCost: Number.isFinite(rentalCost) ? String(rentalCost) : "",
        pendingPayment: Number.isFinite(pending) ? String(pending) : "0",
        dueDate: data.rental?.endDate ? String(data.rental.endDate).slice(0, 10) : "",
        note: ""
      });

      setInvoiceData(data);
      setShowInvoice(false);
      toast('Invoice data loaded successfully', 'success');
    } catch (error) {
      console.error('Error loading invoice data:', error);
      const errorMessage = error.response?.data?.message || 'Failed to load invoice data';
      toast(errorMessage, 'error');
      if (!rentalId) resetForm();
    } finally {
      setLoading(false);
    }
  };

  const resetForm = () => {
    setForm({
      tenantId: "",
      rentalId: "",
      tenantName: "",
      assetName: "",
      location: "",
      unitNumber: "",
      billingPeriod: "",
      rentalCost: "",
      pendingPayment: "",
      dueDate: "",
      note: ""
    });
    setInvoiceData(null);
    setShowInvoice(false);
  };

  const handleChange = (e) => {
    const { name, value } = e.target;
    setForm((current) => ({ ...current, [name]: value }));
  };

  const handleGenerateInvoice = () => {
    if (!form.tenantId) {
      toast('Please select a tenant', 'error');
      return;
    }

    setShowInvoice(true);
    toast('Invoice generated successfully', 'success');

    setTimeout(() => {
      document.getElementById('invoice-preview')?.scrollIntoView({ behavior: 'smooth' });
    }, 100);
  };

  const handlePrintInvoice = () => {
    window.print();
  };

  const invoiceSymbol = "D";
  const money = (val) => {
    const amount = Number(val);
    const safe = Number.isFinite(amount) ? amount : 0;
    return `${invoiceSymbol} ${safe.toLocaleString("en-US", { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;
  };
  const pendingAmount = Number(invoiceData?.payment?.pendingPayment ?? form.pendingPayment);
  const rentalAmount = Number(invoiceData?.payment?.totalDue ?? form.rentalCost);
  const companyPhone = contactText(invoiceData?.company?.phone);
  const companyEmail = contactText(invoiceData?.company?.email);
  const tenantPhone = contactText(invoiceData?.tenant?.phone);
  const tenantEmail = contactText(invoiceData?.tenant?.email);
  const propertyLabel = [form.assetName, form.location].filter(Boolean).join(", ");

  return (
    <div>
      <PageHeader title="Rental Invoice" />

      {/* Form Section */}
      <div className="bg-white rounded-xl border border-gray-200 shadow-sm p-8 mb-6 print:hidden">
        <div className="grid md:grid-cols-2 gap-8">
          {/* LEFT COLUMN */}
          <div className="space-y-6">
            {/* Tenant Name */}
            <div>
              <label className="block text-sm font-semibold text-gray-700 mb-2">
                Tenant Name
              </label>
              <select
                name="tenantId"
                value={form.tenantId}
                onChange={(e) => loadInvoice(e.target.value)}
                disabled={loading}
                className="w-full px-4 py-3 border-b-2 border-gray-300 focus:border-teal-500 outline-none transition-colors bg-transparent disabled:opacity-50"
              >
                <option value="">Select tenant</option>
                {tenants.map((tenant) => (
                  <option key={tenant.id} value={tenant.id}>
                    {tenant.tenantName}
                  </option>
                ))}
              </select>
            </div>

            {invoiceData?.rentals?.length > 1 && (
              <div>
                <label className="block text-sm font-semibold text-gray-700 mb-2">
                  Rental
                </label>
                <select
                  name="rentalId"
                  value={form.rentalId}
                  onChange={(e) => loadInvoice(form.tenantId, e.target.value)}
                  disabled={loading}
                  className="w-full px-4 py-3 border-b-2 border-gray-300 focus:border-teal-500 outline-none transition-colors bg-transparent disabled:opacity-50"
                >
                  {invoiceData.rentals.map((rental) => (
                    <option key={rental.id} value={rental.id}>
                      {rental.assetName || "Asset"} · Unit {rental.unitNumber || "-"} · {rental.occupied ? "Occupied" : "Vacant"}
                    </option>
                  ))}
                </select>
              </div>
            )}

            {/* Asset Name */}
            <div>
              <label className="block text-sm font-semibold text-gray-700 mb-2">
                Asset Name
              </label>
              <input
                type="text"
                name="assetName"
                value={form.assetName}
                disabled
                className="w-full px-4 py-3 border-b-2 border-gray-300 outline-none bg-transparent text-gray-500"
              />
            </div>

            {/* Location */}
            <div>
              <label className="block text-sm font-semibold text-gray-700 mb-2">
                Location
              </label>
              <input
                type="text"
                name="location"
                value={form.location}
                disabled
                className="w-full px-4 py-3 border-b-2 border-gray-300 outline-none bg-transparent text-gray-500"
              />
            </div>

            {/* Unit Number */}
            <div>
              <label className="block text-sm font-semibold text-gray-700 mb-2">
                Unit Number
              </label>
              <input
                type="text"
                name="unitNumber"
                value={form.unitNumber}
                disabled
                className="w-full px-4 py-3 border-b-2 border-gray-300 outline-none bg-transparent text-gray-500"
              />
            </div>

            {/* Billing Period */}
            <div>
              <label className="block text-sm font-semibold text-gray-700 mb-2">
                Billing Period
              </label>
              <input
                type="text"
                name="billingPeriod"
                value={form.billingPeriod}
                disabled
                className="w-full px-4 py-3 border-b-2 border-gray-300 outline-none bg-transparent text-gray-500"
              />
            </div>
          </div>

          {/* RIGHT COLUMN */}
          <div className="space-y-6">
            {/* Rental Cost */}
            <div>
              <label className="block text-sm font-semibold text-gray-700 mb-2">
                Rental Cost
              </label>
              <input
                type="text"
                name="rentalCost"
                value={form.rentalCost !== "" ? money(form.rentalCost) : ""}
                disabled
                className="w-full px-4 py-3 border-b-2 border-gray-300 outline-none bg-transparent text-gray-500 font-semibold"
              />
            </div>

            {/* Pending Payment */}
            <div>
              <label className="block text-sm font-semibold text-gray-700 mb-2">
                Pending Payment
              </label>
              <input
                type="text"
                name="pendingPayment"
                value={form.pendingPayment !== "" ? money(form.pendingPayment) : ""}
                disabled
                className={`w-full px-4 py-3 border-b-2 border-gray-300 outline-none bg-transparent font-bold ${Number(form.pendingPayment) > 0 ? "text-red-600" : "text-gray-500"}`}
              />
            </div>

            {/* Due Date */}
            <div>
              <label className="block text-sm font-semibold text-gray-700 mb-2">
                Due Date
              </label>
              <input
                type="date"
                name="dueDate"
                value={form.dueDate}
                onChange={handleChange}
                className="w-full px-4 py-3 border-b-2 border-gray-300 focus:border-teal-500 outline-none transition-colors bg-transparent"
              />
            </div>

            {/* Note */}
            <div>
              <label className="block text-sm font-semibold text-gray-700 mb-2">
                Note
              </label>
              <textarea
                name="note"
                value={form.note}
                onChange={handleChange}
                rows={7}
                placeholder="Add any notes or payment terms..."
                className="w-full px-4 py-3 border-b-2 border-gray-300 focus:border-teal-500 outline-none transition-colors bg-transparent resize-none"
              />
            </div>
          </div>
        </div>

        {/* Generate Invoice Button */}
        <div className="flex justify-center mt-8">
          <button
            onClick={handleGenerateInvoice}
            disabled={!form.tenantId || loading}
            className="px-12 py-4 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors font-semibold text-lg disabled:opacity-50 disabled:cursor-not-allowed flex items-center gap-3"
          >
            <span className="text-2xl">📄</span>
            Generate Invoice
          </button>
        </div>

        {/* Loading Indicator */}
        {loading && (
          <div className="flex justify-center mt-6">
            <div className="flex items-center gap-3 text-teal-600">
              <div className="animate-spin rounded-full h-6 w-6 border-b-2 border-teal-600"></div>
              <span className="font-semibold">Loading invoice data...</span>
            </div>
          </div>
        )}
      </div>

      {/* Invoice Preview */}
      {showInvoice && invoiceData && (
        <div id="invoice-preview">
          {/* Print Button */}
          <div className="flex justify-end mb-4 print:hidden">
            <button
              onClick={handlePrintInvoice}
              className="px-8 py-3 bg-teal-600 text-white rounded-lg hover:bg-teal-700 transition-colors font-semibold flex items-center gap-2"
            >
              <span>🖨️</span>
              Print Invoice
            </button>
          </div>

          {/* ── INVOICE DOCUMENT ── */}
          <div className="bg-white rounded-xl border border-gray-200 shadow-lg p-5 sm:p-8 md:p-12 print:p-12 max-w-4xl mx-auto print:border-0 print:shadow-none print:rounded-none">

            {/* ── HEADER: logo beside company name ── */}
            <div className="mb-8 pb-4 border-b border-gray-300">
              <div className="flex items-center justify-center gap-4">
                {resolveAssetUrl(invoiceData.company.logo) ? (
                  <img
                    src={resolveAssetUrl(invoiceData.company.logo)}
                    alt="Company Logo"
                    className="h-16 w-16 object-contain"
                  />
                ) : (
                  <div className="w-16 h-16 bg-blue-800 text-white flex items-center justify-center text-2xl font-bold rounded">
                    {invoiceData.company.name.charAt(0)}
                  </div>
                )}

                <div className="text-left">
                  <h1 className="text-2xl font-extrabold text-gray-900 uppercase tracking-wide leading-tight">
                    {invoiceData.company.name}
                  </h1>
                  <p className="text-sm text-gray-600 mt-1">
                    {invoiceData.company.address}
                    {companyPhone && `, Tel:${companyPhone}`}
                  </p>
                  {companyEmail && (
                    <p className="text-sm text-gray-600">
                      Email: {companyEmail}
                    </p>
                  )}
                </div>
              </div>
            </div>

            {/* ── BILL TO  |  INVOICE META ── */}
            <div className="flex flex-col gap-6 sm:flex-row sm:justify-between sm:items-start print:flex-row print:justify-between mb-8">
              {/* Left: Bill To + Property Details */}
              <div className="space-y-4">
                <div>
                  <p className="font-bold text-gray-800">Bill To:</p>
                  <p className="text-gray-700">{invoiceData.tenant.name}</p>
                  {tenantPhone && (
                    <p className="text-gray-600 text-sm">Tel: {tenantPhone}</p>
                  )}
                  {tenantEmail && (
                    <p className="text-gray-600 text-sm">{tenantEmail}</p>
                  )}
                </div>

                <div>
                  <p className="font-bold text-gray-800">Property Details:</p>
                  <p className="text-gray-700 text-sm">
                    Property: {propertyLabel || "-"}
                  </p>
                  <p className="text-gray-700 text-sm">Unit Number: {form.unitNumber || "-"}</p>
                </div>
              </div>

              {/* Right: INVOICE title + meta */}
              <div className="text-left sm:min-w-[220px]">
                <h2 className="text-3xl font-extrabold text-gray-900 mb-2">INVOICE</h2>
                <p className="text-sm text-gray-700">
                  Invoice #: {invoiceData.invoiceNumber}
                </p>
                <p className="text-sm text-gray-700">
                  Date: {formatStoredDate(invoiceData.invoiceDate)}
                </p>
                {form.dueDate && (
                  <p className="text-sm text-gray-700">
                    Due Date: {formatStoredDate(form.dueDate)}
                  </p>
                )}
              </div>
            </div>

            {/* ── PAYMENT TABLE ── */}
            <div className="overflow-x-auto mb-6 print:overflow-visible">
            <table className="w-full min-w-[520px] print:min-w-0 border-collapse text-sm">
              <thead>
                <tr className="bg-gray-100">
                  <th className="border border-gray-400 px-4 py-3 text-center font-bold text-gray-800">
                    Description
                  </th>
                  <th className="border border-gray-400 px-4 py-3 text-center font-bold text-gray-800">
                    Rental Cost<br />({form.billingPeriod})
                  </th>
                  <th className="border border-gray-400 px-4 py-3 text-center font-bold text-gray-800">
                    Pending<br />Payment
                  </th>
                  <th className="border border-gray-400 px-4 py-3 text-center font-bold text-gray-800">
                    Amount Due
                  </th>
                </tr>
              </thead>
              <tbody>
                <tr>
                  <td className="border border-gray-400 px-4 py-3 text-gray-800">
                    Rental Payment
                  </td>
                  <td className="border border-gray-400 px-4 py-3 text-center text-gray-800">
                    {money(rentalAmount)}
                  </td>
                  <td className="border border-gray-400 px-4 py-3 text-center text-gray-800">
                    {money(pendingAmount)}
                  </td>
                  <td className="border border-gray-400 px-4 py-3 text-center text-gray-800">
                    {money(pendingAmount)}
                  </td>
                </tr>
              </tbody>
            </table>
            </div>

            {/* ── TOTAL AMOUNT DUE ── */}
            <div className="flex justify-end mb-8">
              <div className="text-right">
                <p className="font-bold text-gray-800 text-base">
                  Total Amount Due:{" "}
                  <span className="ml-8">
                    {money(pendingAmount)}
                  </span>
                </p>
              </div>
            </div>

            {/* ── PAYMENT NOTE ── */}
            {form.note && (
              <div className="mb-6">
                <p className="font-bold text-gray-800">
                  Payment Note: <span className="font-normal">{form.note}</span>
                </p>
              </div>
            )}

            {/* ── FOOTER ── */}
            <div className="mt-8 pt-4">
              <p className="text-teal-700 font-semibold">Thank you for your business!</p>
            </div>
          </div>
        </div>
      )}

      {/* Print Styles */}
      <style>{`
        @media print {
          body * {
            visibility: hidden;
          }
          #invoice-preview,
          #invoice-preview * {
            visibility: visible;
          }
          #invoice-preview {
            position: absolute;
            left: 0;
            top: 0;
            width: 100%;
          }
          .print\\:hidden {
            display: none !important;
          }
          .print\\:border-0 {
            border: 0 !important;
          }
          .print\\:shadow-none {
            box-shadow: none !important;
          }
          .print\\:rounded-none {
            border-radius: 0 !important;
          }
        }
      `}</style>
    </div>
  );
};

export default Invoice;